/*
 * Purpose: Sticky compose anchor — user outcome is the constant; pipeline stepper
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { Anchor, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COMPOSE_PIPELINE, type ComposeTimelineView } from '@/lib/composeTimeline';
import { Button } from '@/components/ui/button';

interface ComposeStoryAnchorProps {
  timeline: ComposeTimelineView;
  outcomeText: string;
  interpretedGoal?: string | null;
  onJumpToNow?: () => void;
  className?: string;
}

export function ComposeStoryAnchor({
  timeline,
  outcomeText,
  interpretedGoal,
  onJumpToNow,
  className,
}: ComposeStoryAnchorProps) {
  const { currentIndex, isComplete } = timeline;
  const hasOutcome = outcomeText.trim().length > 0;
  const displayOutcome = hasOutcome ? outcomeText.trim() : null;

  return (
    <div
      className={cn(
        'compose-story-anchor relative sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 ab-interactive',
        'border-b border-ab-green-core/20 dark:border-ab-green-glow/25 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/85',
        'pb-2 pt-0.5 mb-3 sm:mb-3 shadow-sm',
        className
      )}
      aria-label="Compose story anchor"
    >
      <div className="max-w-4xl mx-auto w-full space-y-1.5">
        <div className="flex gap-2 items-start">
          <div
            className="compose-anchor-pin flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ab-green-mist/80 dark:bg-ab-green-forest/40 ring-1 ring-ab-green-core/35 dark:ring-ab-green-glow/40"
            aria-hidden
          >
            <Anchor className="h-3.5 w-3.5 text-ab-green-core dark:text-ab-green-glow" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight mb-0.5">
              Your outcome
            </h2>
            <p
              className={cn(
                'text-xs sm:text-sm font-medium leading-snug line-clamp-2',
                hasOutcome ? 'text-foreground' : 'text-muted-foreground italic'
              )}
            >
              {displayOutcome ?? 'Describe what you want — everything else orbits this.'}
            </p>
            {interpretedGoal && hasOutcome && (
              <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1 hidden sm:block">
                Interpreted: {interpretedGoal}
              </p>
            )}
          </div>
          {onJumpToNow && currentIndex > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0 h-7 px-2 text-[11px] hidden sm:inline-flex"
              onClick={onJumpToNow}
            >
              Jump to Now
            </Button>
          )}
        </div>

        <div
          className="compose-pipeline-divider -mx-4 sm:-mx-6 mt-3 sm:mt-3.5"
          role="presentation"
          aria-hidden
        />

        <nav
          aria-label="Compose pipeline"
          className="compose-pipeline-rail relative -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2.5"
        >
          <div className="overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-thin">
            <ol className="flex items-center gap-0 min-w-max sm:min-w-0 sm:w-full">
              {COMPOSE_PIPELINE.map((stage, index) => {
                const isDone = isComplete || index < currentIndex;
                const isCurrent = !isComplete && index === currentIndex;
                const stepState: 'complete' | 'active' | 'inactive' = isCurrent
                  ? 'active'
                  : isDone
                    ? 'complete'
                    : 'inactive';
                const isLast = index === COMPOSE_PIPELINE.length - 1;
                const connectorState: 'complete' | 'active' | 'inactive' =
                  isDone ? 'complete' : isCurrent ? 'active' : 'inactive';

                return (
                  <li
                    key={stage.id}
                    className={cn(
                      'compose-pipeline-step flex items-center min-w-0',
                      !isLast && 'flex-1 min-w-0'
                    )}
                  >
                    <div
                      className={cn('compose-pipeline-node compose-pipeline-node--' + stepState)}
                      title={stage.label}
                    >
                      <span
                        className={cn('compose-pipeline-dot', 'compose-pipeline-dot--' + stepState)}
                        aria-current={stepState === 'active' ? 'step' : undefined}
                        data-step-state={stepState}
                      >
                        {stepState === 'complete' ? (
                          <Check className="compose-pipeline-dot-icon" aria-hidden />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <span
                        className={cn(
                          'compose-pipeline-label',
                          'compose-pipeline-label--' + stepState,
                          stepState !== 'active' && 'hidden sm:inline'
                        )}
                      >
                        {stage.shortLabel}
                      </span>
                    </div>
                    {!isLast && (
                      <div
                        className={cn(
                          'compose-pipeline-connector',
                          'compose-pipeline-connector--' + connectorState
                        )}
                        aria-hidden
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </nav>
      </div>

      <div
        className="compose-anchor-spine pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 w-px h-2 bg-gradient-to-b from-ab-green-core/45 to-ab-green-core/5 dark:from-ab-green-glow/50 dark:to-ab-green-glow/5"
        aria-hidden
      />
    </div>
  );
}
